'use client'

import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { StopOptionType } from '@/src/types/filterType'
import { Box, Button, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React from 'react'

interface StopsFilterProps {
    stopOptions?: StopOptionType[]
    onOptionClick: (option: StopOptionType) => void
    onStopsReset: () => void
    shouldShowReset: boolean
    title: string
}

const StopsFilter: React.FC<StopsFilterProps> = React.memo(
    ({ stopOptions, onOptionClick, onStopsReset, shouldShowReset, title }) => {
        const { formatCurrency } = useFormatCurrencyForSrp()
        const t = useTranslations('common')
        return (
            <Box>
                {/* Stops filter section */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'grey.200' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.900' }}>
                            {title}
                        </Typography>
                        {shouldShowReset && (
                            <Button
                                variant="text"
                                size="small"
                                disableRipple
                                onClick={onStopsReset}
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: 'brand.primary',
                                    p: 0,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        bgcolor: 'transparent'
                                    }
                                }}
                            >
                                {t('reset')}
                            </Button>
                        )}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                        {stopOptions?.map(option => {
                            const { id, label, price, isSelected } = option
                            return (
                                <Button
                                    key={id}
                                    variant={'outlined'}
                                    onClick={() => onOptionClick(option)}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        py: 1,
                                        px: 0.5,
                                        borderRadius: 1,
                                        textAlign: 'center',
                                        transition: 'all 0.2s',
                                        minHeight: 48,
                                        ...(isSelected
                                            ? {
                                                  borderColor: 'link.main',
                                                  color: 'link.main',
                                                  border: '1px solid',
                                                  boxShadow: 'none',
                                                  bgcolor: 'primary.100'
                                              }
                                            : {
                                                  borderColor: 'grey.200',
                                                  bgcolor: 'background.default',
                                                  color: '#333333'
                                              })
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 400,
                                            lineHeight: 1.2,
                                            textTransform: 'none'
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.75rem',
                                            mt: 0.5,
                                            color: isSelected ? 'link.text' : '#333333',
                                            fontWeight: 600,
                                            textTransform: 'none'
                                        }}
                                    >
                                        {formatCurrency(price)}
                                    </Typography>
                                </Button>
                            )
                        })}
                    </Box>
                </Box>
            </Box>
        )
    }
)

StopsFilter.displayName = 'StopsFilter'

export { StopsFilter }
export type { StopsFilterProps }
export default StopsFilter
